import type { ApiErrorBody, ListResponse } from '@gbv/types';

/**
 * The API client for the client portal.
 *
 * The access token lives in memory only — never localStorage, because any XSS
 * bug would otherwise become a full account takeover. The refresh token is an
 * httpOnly cookie the browser sends automatically and JavaScript cannot read.
 */

const BASE_URL = import.meta.env.VITE_API_URL ?? 'http://localhost:4001';
const PORTAL_PREFIX = '/api/client';

export class ApiRequestError extends Error {
  readonly status: number;
  readonly code: string;
  readonly fields?: Record<string, string>;
  readonly used?: number;
  readonly limit?: number;
  /**
   * The whole error body.
   *
   * ApiError on the server can attach arbitrary detail alongside the message —
   * the conflicting appointment on a double-booking, for instance. Naming each
   * one here means a handler silently reads `undefined` until someone
   * remembers to widen this class, so the raw body is kept and `detail()` is
   * the typed way in.
   */
  readonly body: ApiErrorBody & Record<string, unknown>;

  constructor(status: number, body: ApiErrorBody) {
    super(body.message || 'Something went wrong.');
    this.name = 'ApiRequestError';
    this.status = status;
    this.code = body.error;
    this.fields = body.fields;
    this.used = body.used;
    this.limit = body.limit;
    this.body = body as ApiErrorBody & Record<string, unknown>;
  }

  /** Read a server-attached extra off the error body. */
  detail<T>(key: string): T | undefined {
    return this.body[key] as T | undefined;
  }
}

// ── Token and org context, set by the auth store ──────────────

let accessToken: string | null = null;
let activeOrgId: string | null = null;
let onSessionLost: (() => void) | null = null;

export function setAccessToken(token: string | null): void {
  accessToken = token;
}

export function getAccessToken(): string | null {
  return accessToken;
}

export function setActiveOrgId(orgId: string | null): void {
  activeOrgId = orgId;
}

export function setSessionLostHandler(handler: () => void): void {
  onSessionLost = handler;
}

// ── Refresh, single-flight ────────────────────────────────────

let refreshInFlight: Promise<boolean> | null = null;

/**
 * Several queries can 401 at the same instant when a token expires. Without
 * this guard each would fire its own refresh, and rotation would treat the
 * second one as token reuse and kill the session.
 */
async function refreshAccessToken(): Promise<boolean> {
  if (refreshInFlight) return refreshInFlight;

  refreshInFlight = (async () => {
    try {
      const res = await fetch(`${BASE_URL}${PORTAL_PREFIX}/auth/refresh`, {
        method: 'POST',
        credentials: 'include',
      });
      if (!res.ok) return false;

      const body = (await res.json()) as { accessToken: string };
      accessToken = body.accessToken;
      return true;
    } catch {
      return false;
    } finally {
      // Cleared on the next tick so simultaneous callers all see this result.
      setTimeout(() => {
        refreshInFlight = null;
      }, 0);
    }
  })();

  return refreshInFlight;
}

// ── Request ───────────────────────────────────────────────────

export interface RequestOptions extends Omit<RequestInit, 'body'> {
  body?: unknown;
  /** Skip the X-Org-Id header for endpoints that are not org-scoped. */
  skipOrg?: boolean;
  /** Internal: prevents an infinite refresh loop. */
  _isRetry?: boolean;
}

export async function apiRequest<T>(path: string, options: RequestOptions = {}): Promise<T> {
  const { body, skipOrg = false, _isRetry = false, headers, ...rest } = options;

  const isFormData = body instanceof FormData;

  const requestHeaders = new Headers(headers);
  // FormData must set its own Content-Type so the multipart boundary is
  // included — setting it by hand produces an unparseable request.
  if (body !== undefined && !isFormData) requestHeaders.set('Content-Type', 'application/json');
  if (accessToken) requestHeaders.set('Authorization', `Bearer ${accessToken}`);
  if (!skipOrg && activeOrgId) requestHeaders.set('X-Org-Id', activeOrgId);

  const response = await fetch(`${BASE_URL}${PORTAL_PREFIX}${path}`, {
    ...rest,
    headers: requestHeaders,
    credentials: 'include',
    body: body === undefined ? undefined : isFormData ? (body as FormData) : JSON.stringify(body),
  });

  if (response.status === 401 && !_isRetry) {
    const refreshed = await refreshAccessToken();
    if (refreshed) {
      return apiRequest<T>(path, { ...options, _isRetry: true });
    }
    accessToken = null;
    onSessionLost?.();
  }

  if (response.status === 204) return undefined as T;

  const payload = await response.json().catch(() => ({
    error: 'INTERNAL',
    message: 'The server sent something we could not read.',
  }));

  if (!response.ok) throw new ApiRequestError(response.status, payload as ApiErrorBody);

  return payload as T;
}

export const api = {
  get: <T>(path: string, options?: RequestOptions) =>
    apiRequest<T>(path, { ...options, method: 'GET' }),
  post: <T>(path: string, body?: unknown, options?: RequestOptions) =>
    apiRequest<T>(path, { ...options, method: 'POST', body }),
  patch: <T>(path: string, body?: unknown, options?: RequestOptions) =>
    apiRequest<T>(path, { ...options, method: 'PATCH', body }),
  put: <T>(path: string, body?: unknown, options?: RequestOptions) =>
    apiRequest<T>(path, { ...options, method: 'PUT', body }),
  /** Multipart upload — pass a FormData, not a plain object. */
  postForm: <T>(path: string, form: FormData, options?: RequestOptions) =>
    apiRequest<T>(path, { ...options, method: 'POST', body: form }),
  delete: <T>(path: string, options?: RequestOptions) =>
    apiRequest<T>(path, { ...options, method: 'DELETE' }),
};

/** Build a query string, dropping empty values so the URL stays readable. */
export function qs<T extends object>(params: T): string {
  const search = new URLSearchParams();
  for (const [key, value] of Object.entries(params)) {
    if (value === undefined || value === null || value === '') continue;
    search.set(key, value instanceof Date ? value.toISOString() : String(value));
  }
  const result = search.toString();
  return result ? `?${result}` : '';
}

export type { ListResponse };

/**
 * The most useful sentence an error carries, for a toast.
 *
 * A validation failure's top-level message is "Check the highlighted fields",
 * which is true beside a form and useless in a toast that has no fields to
 * highlight. The per-field message is the one that says what was wrong.
 */
export function describeApiError(err: unknown, fallback = 'Try again in a moment.'): string {
  if (!(err instanceof ApiRequestError)) return fallback;

  const field = err.fields && Object.values(err.fields)[0];
  return field ?? err.message ?? fallback;
}
